import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from './AdminLayout';
import PortfolioForm from './PortfolioForm';
import { PortfolioApi } from '../../api/adminApi';
import { useServicesList } from '../../hooks/useServicesList';
import './PortfoliosAdmin.css';

const normalizeAlias = (value) => {
  if (!value) return '';
  return value.trim().toLowerCase();
};

const PortfoliosAdmin = () => {
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentPortfolio, setCurrentPortfolio] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [serviceFilter, setServiceFilter] = useState('');
  const { services, isLoading: servicesLoading } = useServicesList();

  const serviceLookup = useMemo(() => {
    const map = new Map();
    services.forEach((svc) => map.set(svc.id, svc));
    return map;
  }, [services]);

  const aliasLookup = useMemo(() => {
    const map = new Map();
    services.forEach((svc) => map.set(svc.alias, svc));
    return map;
  }, [services]);

  const loadPortfolios = async (alias = serviceFilter) => {
    setLoading(true);
    setError('');
    try {
      const normalized = normalizeAlias(alias);
      const data = await PortfolioApi.getAll(normalized ? { serviceAlias: normalized } : {});
      setPortfolios(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to load portfolios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPortfolios(serviceFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceFilter]);

  const handleCreate = () => {
    setCurrentPortfolio(null);
    setIsFormOpen(true);
  };

  const handleEdit = (portfolio) => {
    setCurrentPortfolio(portfolio);
    setIsFormOpen(true);
  };

  const handleDelete = async (portfolio) => {
    const confirmed = window.confirm(`Delete portfolio "${portfolio.name}"?`);
    if (!confirmed) return;
    try {
      await PortfolioApi.remove(portfolio.id);
      await loadPortfolios();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to delete portfolio');
    }
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setCurrentPortfolio(null);
  };

  const handleSubmit = async (payload) => {
    setIsSaving(true);
    try {
      if (currentPortfolio) {
        await PortfolioApi.update(currentPortfolio.id, payload);
      } else {
        await PortfolioApi.create(payload);
      }
      await loadPortfolios();
      closeForm();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to save portfolio');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    closeForm();
  };

  const renderTable = () => {
    if (loading) {
      return <p>Loading portfolios...</p>;
    }

    if (!portfolios.length) {
      return <p>No portfolios found.</p>;
    }

    return (
      <table className="portfolio-table">
        <thead>
          <tr>
            <th>Project</th>
            <th>Service</th>
            <th>Slug</th>
            <th>Order</th>
            <th>Company</th>
            <th>Updated</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {portfolios.map((portfolio) => (
            <tr key={portfolio.id}>
              <td>
                <div className="portfolio-table-title">{portfolio.name}</div>
                <p className="portfolio-table-description">{portfolio.description}</p>
              </td>
              <td>{portfolio.serviceAlias ? aliasLookup.get(portfolio.serviceAlias)?.name || portfolio.serviceAlias : 'Unassigned'}</td>
              <td>{portfolio.slug}</td>
              <td>{typeof portfolio.sortOrder === 'number' ? portfolio.sortOrder : 0}</td>
              <td>{portfolio.company || '-'}</td>
              <td>{portfolio.updatedAt ? new Date(portfolio.updatedAt).toLocaleDateString() : '-'}</td>
              <td>
                <div className="table-actions">
                  <button onClick={() => handleEdit(portfolio)}>Edit</button>
                  <button className="danger" onClick={() => handleDelete(portfolio)}>Delete</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const canCreatePortfolio = services.length > 0;

  return (
    <AdminLayout
      title="Portfolios"
      description="Showcase your case studies across each service page."
      actions={!isFormOpen && (
        <button className="admin-primary-btn" onClick={handleCreate} disabled={!canCreatePortfolio}>
          + Add Portfolio
        </button>
      )}
    >
      {error && <div className="admin-alert">{error}</div>}

      {!isFormOpen && (
        <div className="portfolio-filters">
          <label htmlFor="serviceFilter">Filter by service</label>
          <select
            id="serviceFilter"
            value={serviceFilter}
            onChange={(event) => setServiceFilter(event.target.value)}
            disabled={servicesLoading}
          >
            <option value="">All services</option>
            {services.map((svc) => (
              <option key={svc.id} value={svc.alias}>{svc.name}</option>
            ))}
          </select>
          {!canCreatePortfolio && (
            <p className="portfolio-helper-text">Add a service first to create case studies.</p>
          )}
        </div>
      )}

      {isFormOpen ? (
        <PortfolioForm
          initialData={currentPortfolio}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSaving={isSaving}
          services={services}
        />
      ) : (
        <div className="portfolio-table-wrapper">{renderTable()}</div>
      )}
    </AdminLayout>
  );
};

export default PortfoliosAdmin;
